/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { FileUpload } from '@/components/invoices/FileUpload'
import { UploadProgress } from '@/components/invoices/UploadProgress'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getDocumentTypes, getCenters, uploadDocument } from '@/api/invoices/invoices.api'
import type { UploadProgress as UploadProgressType } from '@/types/invoices.types'

export default function UploadDocumentsPage() {
  const [files, setFiles] = useState<File[]>([])
  const [selectedType, setSelectedType] = useState<string>('')
  const [selectedCenter, setSelectedCenter] = useState<string>('')
  const [uploads, setUploads] = useState<UploadProgressType[]>([])

  // Načtení typů dokumentů
  const { data: documentTypes, isLoading: isLoadingTypes } = useQuery({
    queryKey: ['documentTypes'],
    queryFn: getDocumentTypes,
  })

  // Načtení středisek
  const { data: centers, isLoading: isLoadingCenters } = useQuery({
    queryKey: ['centers'],
    queryFn: getCenters,
  })

  // Automatické předvybrání první hodnoty po načtení dat
  useEffect(() => {
    if (documentTypes && documentTypes.length > 0 && !selectedType) {
      setSelectedType(documentTypes[0].code)
    }
  }, [documentTypes, selectedType])

  useEffect(() => {
    if (centers && centers.length > 0 && !selectedCenter) {
      setSelectedCenter(centers[0].id)
    }
  }, [centers, selectedCenter])

  const handleUpload = async () => {
    if (!selectedType || !selectedCenter) {
      toast.error('Vyberte typ dokumentu a středisko')
      return
    }

    if (files.length === 0) {
      toast.error('Vyberte alespoň jeden soubor')
      return
    }

    // Inicializace progress pro všechny soubory
    const initialUploads: UploadProgressType[] = files.map((file) => ({
      file,
      status: 'pending',
      progress: 0,
    }))
    setUploads(initialUploads)

    // Nahrávání souborů jednotlivě
    for (let i = 0; i < files.length; i++) {
      const file = files[i]

      try {
        // Nastavení stavu na uploading
        setUploads((prev) =>
          prev.map((upload, index) =>
            index === i ? { ...upload, status: 'uploading', progress: 50 } : upload
          )
        )

        // Nahrání souboru
        await uploadDocument(file, selectedCenter, selectedType)

        // Nastavení stavu na success
        setUploads((prev) =>
          prev.map((upload, index) =>
            index === i ? { ...upload, status: 'success', progress: 100 } : upload
          )
        )
      } catch (error: unknown) {
        // Nastavení stavu na error
        setUploads((prev) =>
          prev.map((upload, index) =>
            index === i
              ? {
                  ...upload,
                  status: 'error',
                  error:
                    error && typeof error === 'object' && 'response' in error
                      ? (error as { response?: { data?: { message?: string } } }).response?.data
                          ?.message || 'Chyba při nahrávání'
                      : 'Chyba při nahrávání',
                }
              : upload
          )
        )
      }
    }

    // Kontrola, zda všechny soubory byly úspěšně nahrány
    const allSuccess = uploads.every((upload) => upload.status === 'success')
    if (allSuccess) {
      toast.success('Všechny soubory byly úspěšně nahrány')
      setFiles([])
      setUploads([])
    } else {
      const successCount = uploads.filter((upload) => upload.status === 'success').length
      toast.warning(`Nahráno ${successCount} z ${files.length} souborů`)
    }
  }

  const isUploading = uploads.some((upload) => upload.status === 'uploading')

  return (
    <div className="container mx-auto py-6 max-w-4xl">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Nahrát dokumenty</h1>
          <p className="text-muted-foreground mt-1">
            Nahrajte dokumenty do systému
          </p>
        </div>

        {/* Formulář */}
        <div className="bg-white p-6 rounded-lg border space-y-6">
          {/* Výběr typu dokumentu a střediska */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="document-type">Typ dokumentu *</Label>
              <Select
                value={selectedType}
                onValueChange={setSelectedType}
                disabled={isLoadingTypes || isUploading}
              >
                <SelectTrigger id="document-type">
                  <SelectValue placeholder="Vyberte typ dokumentu" />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes?.map((type) => (
                    <SelectItem key={type.code} value={type.code}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="center">Středisko *</Label>
              <Select
                value={selectedCenter}
                onValueChange={setSelectedCenter}
                disabled={isLoadingCenters || isUploading}
              >
                <SelectTrigger id="center">
                  <SelectValue placeholder="Vyberte středisko" />
                </SelectTrigger>
                <SelectContent>
                  {centers?.map((center) => (
                    <SelectItem key={center.id} value={center.id}>
                      {center.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* File Upload */}
          <FileUpload
            files={files}
            onFilesChange={setFiles}
            disabled={isUploading}
          />

          {/* Upload Progress */}
          <UploadProgress uploads={uploads} />

          {/* Tlačítko nahrát */}
          <div className="flex justify-end">
            <Button
              onClick={handleUpload}
              disabled={
                !selectedType ||
                !selectedCenter ||
                files.length === 0 ||
                isUploading
              }
              size="lg"
            >
              {isUploading ? 'Nahrávání...' : 'Nahrát dokumenty'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
